import React from 'react';
import { Shield, Lock, Eye, Database, Users, Globe, Mail } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdated = 'April 27, 2025';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-lg text-gray-600">
            How Therapist Direct collects, uses, shares, and protects your information
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="prose prose-lg max-w-none">
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Lock className="h-6 w-6 text-blue-600 mr-3" />
                1. Introduction
              </h2>
              <p className="text-gray-700 mb-4">
                This Privacy Policy ("Policy") describes how Therapist Direct, Inc. ("Therapist Direct," "we," "our," or "us") collects, uses, shares, and protects information when you use our website, applications, and related services (the "Platform").
              </p>
              <p className="text-gray-700 mb-4">
                This Policy applies to both:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>Consumers (individuals searching for therapy providers), and</li>
                <li>Providers (licensed practices and clinicians who list on the Platform).</li>
              </ul>
              <p className="text-gray-700">
                By using the Platform, you agree to this Policy and to our Terms of Use. If you do not agree, you may not use the Platform.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Database className="h-6 w-6 text-blue-600 mr-3" />
                2. What We Collect
              </h2>
              <p className="text-gray-700 mb-4">
                We collect the following categories of information:
              </p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">a. Information You Provide</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li><strong>Consumer Accounts:</strong> Name, email address, contact preferences, search criteria (e.g., insurance, location, specialties sought).</li>
                <li><strong>Provider Accounts:</strong> Practice and clinician details, professional credentials, contact information, business payment details, and profile content you choose to submit.</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">b. Information from Integrations</h3>
              <p className="text-gray-700 mb-4">
                If a Provider connects their EHR system to the Platform, Therapist Direct may access aggregate, non-identifiable operational data such as appointment volumes or scheduling availability.
              </p>
              <p className="text-gray-700 mb-4">
                We do not access, collect, or store any patient medical records or Protected Health Information (PHI).
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">c. Automatic Data Collection</h3>
              <p className="text-gray-700 mb-4">
                When you use the Platform, we automatically collect technical and usage information, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Device and browser type</li>
                <li>IP address and geolocation (approximate)</li>
                <li>Pages viewed, search queries, and actions on the Platform</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Eye className="h-6 w-6 text-blue-600 mr-3" />
                3. How We Use Information
              </h2>
              <p className="text-gray-700 mb-4">
                We use collected information to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Provide, maintain, and improve the Platform.</li>
                <li>Deliver best-match results for Consumers based on their criteria.</li>
                <li>Enable Providers to manage profiles, billing, and visibility.</li>
                <li>Conduct analytics to improve search accuracy and platform performance.</li>
                <li>Communicate with you about your account, listings, or updates.</li>
                <li>Ensure compliance with applicable laws and platform rules.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="h-6 w-6 text-blue-600 mr-3" />
                4. How We Share Information
              </h2>
              <p className="text-gray-700 mb-4">
                We may share information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li><strong>With Providers:</strong> Consumer inquiries are shared with Providers you choose to contact.</li>
                <li><strong>With Consumers:</strong> Provider profile content you submit is visible to Consumers.</li>
                <li><strong>With Service Providers:</strong> Third-party vendors that support our operations (e.g., hosting, payments, analytics).</li>
                <li><strong>For Legal Reasons:</strong> To comply with applicable laws, regulations, or valid legal process.</li>
                <li><strong>In Business Transactions:</strong> If we are acquired, merged, or undergo a similar corporate transaction.</li>
              </ul>
              <p className="text-gray-700">
                We do not sell or rent your personal information to third parties.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Globe className="h-6 w-6 text-blue-600 mr-3" />
                5. Cookies &amp; Tracking
              </h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar tools to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>Remember preferences</li>
                <li>Improve search functionality</li>
                <li>Track analytics on site performance</li>
              </ul>
              <p className="text-gray-700">
                You may disable cookies in your browser, but some features of the Platform may not function properly.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="h-6 w-6 text-blue-600 mr-3" />
                6. Data Security
              </h2>
              <p className="text-gray-700">
                We use reasonable administrative, technical, and physical safeguards to protect your information. However, no system is completely secure, and we cannot guarantee absolute security.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Database className="h-6 w-6 text-blue-600 mr-3" />
                7. Data Retention
              </h2>
              <p className="text-gray-700">
                We retain information for as long as necessary to provide the Platform, comply with legal obligations, and resolve disputes. Providers may request deletion of their account and related data by contacting us at <a href="mailto:support@therapistdirect.com" className="text-blue-600 hover:text-blue-800">support@therapistdirect.com</a>.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="h-6 w-6 text-blue-600 mr-3" />
                8. Children's Privacy
              </h2>
              <p className="text-gray-700">
                The Platform is not intended for children under 13. We do not knowingly collect information from children. If we learn we have collected such data, we will delete it.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Globe className="h-6 w-6 text-blue-600 mr-3" />
                9. Governing Law
              </h2>
              <p className="text-gray-700">
                This Policy is governed by the laws of the State of Wyoming, without regard to its conflict of law principles.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Lock className="h-6 w-6 text-blue-600 mr-3" />
                10. Changes to this Policy
              </h2>
              <p className="text-gray-700">
                We may update this Policy from time to time. Changes will be posted here with an updated effective date. Continued use of the Platform after changes are posted constitutes acceptance of the revised Policy.
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 rounded-lg p-6 mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Us</h3>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <Mail className="h-5 w-5 text-blue-600 mr-2" />
                  <a href="mailto:support@therapistdirect.com" className="text-blue-600 hover:text-blue-800">
                    support@therapistdirect.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;